<template>
  <div class="sidebar-container">
    <!-- Mobile top bar -->
    <div class="mobile-header">
      <button class="toggle-btn" @click="toggleSidebar">
        <VasionMenu />
      </button>
      <h1 v-if="isMobileView" class="sidebar-mobile-header-title">{{ title }}</h1>
    </div>
    <!-- Sidebar panel -->
    <aside :class="{ sidebar: true, open: isOpen }">
      <!-- Close button on mobile -->
      <button class="close-btn" @click="closeSidebar" aria-label="Close sidebar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" role="img" focusable="false">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <!-- Header slot -->
      <div class="sidebar-header" :class="{ 'sidebar-mobile-header': isMobileView }">
        <h1 v-if="!isMobileView" class="sidebar-header-title">{{ title }}</h1>
        <slot name="header"></slot>
      </div>
      <!-- Main navigation -->
      <nav class="sidebar-nav">
        <slot name="navigation">
          <template v-for="(item, index) in navigationOptions">
            <!-- Section header -->
            <h3 v-if="item.type === 'section'" :key="`section-${index}`" class="nav-section">
              {{ item.label }}
            </h3>
            <!-- Navigation link - updated click handler -->
            <router-link
              v-if="item.type === 'link'"
              :key="`link-${index}`"
              :to="item.route"
              class="nav-link"
              @click.native="handleLinkClick"
            >
              <component v-if="item.icon" :is="item.icon" class="nav-icon" />
              <span>{{ item.label }}</span>
            </router-link>
          </template>
        </slot>
      </nav>
      <!-- Footer slot -->
      <div class="sidebar-footer">
        <div class="footer-nav">
          <!-- Help -->
          <button class="footer-nav-item" @click="toggleComponent('help', $event)">
            <div class="footer-item-icon">
              <VasionMenuHelpIcon />
            </div>
            <span class="footer-item-label">Help</span>
            <div v-if="activeComponent === 'help'" class="component-popup">
              <TheHelpMenu />
            </div>
          </button>
          <!-- Profile -->
          <button class="footer-nav-item" @click="toggleComponent('profile', $event)">
            <UserAvatar :initials="avatarText" :name="fullName" />
            <div v-if="activeComponent === 'profile'" class="component-popup">
              <TheProfileMenu :currentLayoutIsPortal="true" />
            </div>
          </button>
        </div>
      </div>
    </aside>
    <div v-if="isOpen" class="overlay" @click="closeSidebar"></div>
  </div>
</template>

<script>
import TheHelpMenu from '@/components/mainViews/TheHelpMenu.vue'
import TheProfileMenu from '@/components/mainViews/TheProfileMenu.vue'
import UserAvatar from '@/components/mainViews/UserAvatar.vue'

export default {
  name: 'PortalSidebar',
  components: {
    TheHelpMenu,
    TheProfileMenu,
    UserAvatar,
  },
  props: {
    navigationOptions: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'WORKSPACE',
    },
  },
  data() {
    return {
      activeComponent: null,
      internalOpen: false,
      isMobileView: false,
    }
  },
  mounted() {
    this.checkMobileView()
    window.addEventListener('resize', this.checkMobileView)
    document.addEventListener('click', this.handleOutsideClick)

    this.$router.afterEach(() => {
      if (this.isMobileView && this.isOpen) {
        this.closeSidebar()
      }
    })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobileView)
    document.removeEventListener('click', this.handleOutsideClick)
  },
  computed: {
    avatarText() {
      const { firstName, lastName, userName } = this.$store.state.common

      if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()

      if (firstName) return firstName.slice(0, 2).toUpperCase()

      return userName ? userName.slice(0, 2).toUpperCase() : ''
    },
    fullName() {
      const { firstName, lastName, userName } = this.$store.state.common

      if (firstName && lastName) return `${firstName} ${lastName}`

      if (firstName) return firstName

      return userName || ''
    },
    isOpen() {
      return this.internalOpen
    },
  },
  methods: {
    checkMobileView() {
      this.isMobileView = window.innerWidth < 960
    },
    closeSidebar() {
      this.internalOpen = false
    },
    handleLinkClick() {
      if (this.isMobileView) {
        this.$nextTick(() => {
          this.closeSidebar()
        })
      }
    },
    handleOutsideClick(event) {
      if (!this.activeComponent) return

      const isClickInComponent = event.target.closest('.component-popup') !== null
      const isClickInButton = event.target.closest('button.footer-nav-item') !== null

      if (isClickInComponent || isClickInButton) {
        return
      }

      this.activeComponent = null
    },
    toggleSidebar() {
      this.internalOpen = !this.internalOpen
    },
    toggleComponent(index, event) {
      if (event) event.stopPropagation()

      this.activeComponent = this.activeComponent === index ? null : index
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/light-theme-system-palette.scss';
@import '@/assets/css/vuetify3Breakpoints.scss';
@import '@/assets/css/variables.scss';

.sidebar-container {
  position: relative;
  width: 100%;
  height: auto;
}

.mobile-header {
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  position: sticky;
  top: 0;
}

.toggle-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  margin-right: 8px;

  svg {
    vertical-align: middle;
  }
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  transform: translateX(-100%);
  background-color: $surface;
  display: flex;
  flex-direction: column;
  padding: 16px;
  transition: transform 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  &.open {
    transform: translateX(0) !important;
    z-index: 2100;
  }
}

.close-btn {
  display: block;
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;

  @include breakpoint(md) {
    display: none;
  }
}

.sidebar-header {
  margin-bottom: 16px;
}
.sidebar-mobile-header {
  margin-top: 36px;
}
.sidebar-mobile-header-title {
  display: flex;
  height: 56px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;

  margin: unset;

  overflow: hidden;
  color: $primary;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: $font-family-accent;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  text-transform: uppercase;
}
.sidebar-header-title {
  display: flex;
  height: 70px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;

  margin: unset;

  overflow: hidden;
  color: $primary;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: $font-family-accent;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  text-transform: uppercase;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  a {
    display: block;
    padding: 8px 0;
    color: inherit;
    text-decoration: none !important;
  }

  .nav-section {
    display: flex;
    padding: 10px 16px;
    align-items: center;
    gap: 10px;
    align-self: stretch;

    margin: unset;

    color: $on-surface-variant;
    font-family: Archivo;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-transform: uppercase;

    &:first-child {
      margin-top: 0;
    }
  }

  .nav-link {
    display: flex;
    height: 40px;
    padding-left: 32px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    position: relative;
    color: $on-surface-variant !important;
    text-decoration: none !important;
    transition: all 0.2s ease;
    border-radius: 4px;
    border: 1px solid transparent;

    .nav-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;

      svg {
        width: 24px;
        height: 24px;
        fill: $on-surface-variant;
        transition: fill 0.2s ease;
        display: block;
      }
    }

    &:hover {
      background-color: rgba($primary, 0.06);
    }

    &.router-link-active {
      color: $primary;
      font-weight: 500;
      background-color: rgba($primary, 0.12);
      border: 1px solid $primary;
      text-decoration: none !important;

      .nav-icon svg {
        fill: $primary;
      }
    }

    &.router-link-exact-active {
      color: $primary !important;
      font-weight: 600;
      text-decoration: none !important;
    }
  }
}

.sidebar-footer {
  margin-top: auto;
  padding-top: 16px;
}

.component-popup {
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 1000;
  min-width: 200px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);

  @include breakpoint(md) {
    display: none;
  }
}

@include breakpoint(md) {
  .sidebar-container {
    width: 280px;
    height: 100%;
  }

  .mobile-header {
    display: none;
  }

  .sidebar {
    position: relative;
    transform: none;
    height: 100%;
    box-shadow: none;
  }
}

.footer-nav-item {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 8px;

  .footer-item-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      max-width: 40px;
      max-height: 40px;
      fill: $on-surface-variant;
      display: block;
    }
  }

  .footer-item-label {
    color: $on-surface-variant;
    flex: 1;
    padding-left: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: $font-family-book;
    font-size: 14px;
    font-weight: bold;
    line-height: 20px;
    letter-spacing: 0.1px;
  }
}

button.footer-nav-item {
  background: transparent;
  border: none;
  text-align: left;
  width: 100%;
  padding: 8px 0;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:focus {
    outline: 2px solid $primary;
    border-radius: 4px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
}
</style>
